import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaTelegram, FaSpinner } from 'react-icons/fa';
import { FiCheck, FiGlobe } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { isChineseEnabled } from '../i18n';

// 从环境变量获取配置
const APP_NAME = import.meta.env.VITE_APP_NAME || 'PolySports';

// 根据配置动态生成语言列表
const ALL_LANGUAGES = [
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
];
const LANGUAGES = isChineseEnabled 
  ? ALL_LANGUAGES 
  : ALL_LANGUAGES.filter(lang => lang.code !== 'zh');

const normalizeLang = (lang) => {
  const lowered = (lang || 'en').toLowerCase();
  if (lowered.startsWith('zh')) return isChineseEnabled ? 'zh' : 'en';
  if (lowered.startsWith('ja')) return 'ja';
  if (lowered.startsWith('es')) return 'es';
  if (lowered.startsWith('en')) return 'en';
  return 'en';
};

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithTokens, isAuthenticated } = useAuth();

  const [status, setStatus] = useState('idle'); // idle | waiting | success | error
  const [error, setError] = useState(null);
  const [ticketId, setTicketId] = useState(null);
  const [loginMethod, setLoginMethod] = useState('telegram');
  const [emailValue, setEmailValue] = useState('');
  const [emailTicketId, setEmailTicketId] = useState(null);
  const [emailCode, setEmailCode] = useState('');
  const [emailStep, setEmailStep] = useState('idle'); // idle | sending | sent | verifying
  const pollIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  const currentLang = normalizeLang(i18n.language);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setLangMenuOpen(false);
  };

  // 如果已登录，重定向
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // 检查 URL 参数中的登录凭证（旧方式兼容）
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ticketIdParam = params.get('ticket_id');
    const userId = params.get('user_id');
    const code = params.get('code');

    if (ticketIdParam && userId && code) {
      handleVerifyOldWay(ticketIdParam, userId, code);
    }
  }, [location.search]);

  // 清理轮询
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setError(null);
    if (loginMethod === 'telegram') {
      setEmailStep('idle');
      setEmailTicketId(null);
      setEmailCode('');
    } else {
      setStatus('idle');
      setTicketId(null);
    }
  }, [loginMethod]);

  // 轮询检查 ticket 状态
  const startPolling = (ticketId) => {
    // 清理之前的轮询
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    pollIntervalRef.current = setInterval(async () => {
      try {
        const data = await api.checkLoginTicket(ticketId);
        if (data.code === 'SUCCESS') {
          const { status: ticketStatus } = data.data;

          if (ticketStatus === 'confirmed') {
            // 登录成功！
            clearInterval(pollIntervalRef.current);
            clearTimeout(timeoutRef.current);

            // 保存 tokens
            loginWithTokens(data.data);
            setStatus('success');

            // 重定向
            const from = location.state?.from?.pathname || '/';
            navigate(from, { replace: true });
          } else if (ticketStatus === 'expired') {
            clearInterval(pollIntervalRef.current);
            clearTimeout(timeoutRef.current);
            setStatus('idle');
            setError(t('auth.loginExpired'));
          }
          // pending 状态继续轮询
        }
      } catch (err) {
        console.error('Poll error:', err);
        // 继续轮询
      }
    }, 2000); // 每 2 秒检查一次

    // 5 分钟后停止轮询
    timeoutRef.current = setTimeout(() => {
      clearInterval(pollIntervalRef.current);
      setStatus('idle');
      setError(t('auth.loginTimeout'));
    }, 5 * 60 * 1000);
  };

  const handleTelegramLogin = async () => {
    setStatus('waiting');
    setError(null);

    try {
      // 1. 调用后端创建 ticket
      const data = await api.createLoginTicket(i18n.language);
      console.log('createLoginTicket response:', data);

      if (data.code !== 'SUCCESS') {
        throw new Error(data.msg || t('auth.loginFailed'));
      }

      const { ticket_id, bot_link } = data.data;
      console.log('ticket_id:', ticket_id);
      console.log('bot_link:', bot_link);
      setTicketId(ticket_id);

      // 2. 打开 Bot 链接
      window.open(bot_link, '_blank');

      // 3. 开始轮询检查状态
      startPolling(ticket_id);
    } catch (err) {
      console.error('handleTelegramLogin error:', err);
      setStatus('error');
      setError(err.message || t('auth.loginFailed'));
    }
  };

  // 旧方式验证（URL 参数方式）
  const handleVerifyOldWay = async (ticketId, userId, code) => {
    try {
      setStatus('waiting');
      const data = await api.verifyLogin(ticketId, userId, code);

      if (data.code === 'SUCCESS') {
        loginWithTokens(data.data);
        setStatus('success');

        // 重定向
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
      } else {
        throw new Error(data.msg || t('auth.loginFailed'));
      }
    } catch (err) {
      setStatus('error');
      setError(err.message || t('auth.loginFailed'));
    }
  };

  const handleCancel = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setStatus('idle');
    setTicketId(null);
  };

  const handleEmailSendCode = async () => {
    if (!emailValue) {
      setError(t('auth.emailRequired'));
      return;
    }
    setEmailStep('sending');
    setError(null);
    try {
      const data = await api.requestEmailLoginCode(emailValue, i18n.language);
      if (data.code !== 'SUCCESS') {
        throw new Error(data.msg || t('auth.loginFailed'));
      }
      setEmailTicketId(data.data.ticket_id);
      setEmailStep('sent');
    } catch (err) {
      setEmailStep('idle');
      const backendCode = err?.payload?.code;
      if (backendCode === 'EMAIL_NOT_BOUND') {
        setError(t('auth.emailNotBound'));
      } else {
        setError(err.message || t('auth.loginFailed'));
      }
    }
  };

  const handleEmailVerify = async () => {
    if (!emailTicketId) {
      setError(t('auth.loginFailed'));
      return;
    }
    if (!emailCode) {
      setError(t('auth.codeRequired'));
      return;
    }
    setEmailStep('verifying');
    setError(null);
    try {
      const data = await api.verifyEmailLogin(emailValue, emailTicketId, emailCode);
      if (data.code === 'SUCCESS') {
        loginWithTokens(data.data);
        setStatus('success');
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
        return;
      }
      throw new Error(data.msg || t('auth.loginFailed'));
    } catch (err) {
      setEmailStep('sent');
      setError(err.message || t('auth.loginFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="glass-card p-8 w-full max-w-md text-center">
        <div className="flex items-center justify-end mb-4">
          <div
            ref={langMenuRef}
            className="relative z-[100]"
            onMouseEnter={() => setLangMenuOpen(true)}
            onMouseLeave={() => setLangMenuOpen(false)}
          >
            <button
              className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-glass-hover text-gray-300 hover:text-white transition-colors border border-transparent hover:border-glass-border"
              aria-label={t('auth.switchLang')}
            >
              <FiGlobe className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wider">{currentLang}</span>
            </button>

            {langMenuOpen && (
              <div className="absolute right-0 top-full pt-1 z-[100]">
                <div className="py-1 min-w-[140px] bg-surface border border-glass-border rounded-xl shadow-lg backdrop-blur-glass overflow-hidden">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        currentLang === lang.code
                          ? 'bg-accent-strong/20 text-accent'
                          : 'text-gray-300 hover:bg-glass-hover hover:text-white'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span className="flex-1 text-left">{lang.label}</span>
                      {currentLang === lang.code && (
                        <FiCheck className="w-4 h-4 text-accent" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <h1 className="text-2xl font-bold text-accent mb-2">{APP_NAME}</h1>
        <p className="text-gray-400 mb-8">{t('auth.loginSubtitle')}</p>

        {error && (
          <div className="mb-4 p-3 bg-negative/15 border border-negative/40 rounded-lg text-negative text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center justify-center gap-2 mb-6">
          <button
            onClick={() => setLoginMethod('telegram')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              loginMethod === 'telegram'
                ? 'bg-accent/20 text-accent border border-accent/30'
                : 'bg-surface/70 text-slate-400 border border-glass-border'
            }`}
          >
            {t('auth.loginWithTelegram')}
          </button>
          <button
            onClick={() => setLoginMethod('email')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              loginMethod === 'email'
                ? 'bg-accent/20 text-accent border border-accent/30'
                : 'bg-surface/70 text-slate-400 border border-glass-border'
            }`}
          >
            {t('auth.loginWithEmail')}
          </button>
        </div>

        {loginMethod === 'telegram' ? (
          status === 'waiting' ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-gray-400">
                <FaSpinner className="animate-spin" />
                {t('auth.waitingConfirm')}
              </div>
              <p className="text-gray-500 text-sm">
                {t('auth.waitingDesc')}
              </p>
              <button
                onClick={handleCancel}
                className="text-accent hover:text-accent-soft text-sm"
              >
                {t('auth.cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={handleTelegramLogin}
              className="w-full bg-[#0088cc] hover:bg-[#0077b5] text-white py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <FaTelegram size={20} />
              {t('auth.loginWithTelegram')}
            </button>
          )
        ) : (
          <div className="space-y-4 text-left">
            <div className="text-xs text-slate-400">{t('auth.emailLoginHint')}</div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('auth.emailLabel')}</label>
              <input
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="w-full bg-surface-highlight/50 border border-glass-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            {emailStep === 'sent' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('auth.codeLabel')}</label>
                <input
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value)}
                  placeholder={t('auth.codePlaceholder')}
                  className="w-full bg-surface-highlight/50 border border-glass-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-accent"
                />
              </div>
            )}
            {emailStep !== 'sent' ? (
              <button
                onClick={handleEmailSendCode}
                disabled={emailStep === 'sending'}
                className="w-full bg-surface-highlight hover:bg-surface text-white py-3 px-6 rounded-lg font-medium transition-colors disabled:opacity-60"
              >
                {emailStep === 'sending' ? t('auth.sendingCode') : t('auth.sendCode')}
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleEmailVerify}
                  disabled={emailStep === 'verifying'}
                  className="flex-1 bg-accent-strong hover:bg-accent text-white py-3 px-6 rounded-lg font-medium transition-colors disabled:opacity-60"
                >
                  {emailStep === 'verifying' ? t('auth.verifying') : t('auth.verifyCode')}
                </button>
                <button
                  onClick={handleEmailSendCode}
                  className="px-4 py-3 rounded-lg border border-glass-border text-slate-300 text-sm hover:text-white"
                >
                  {t('auth.resendCode')}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 text-gray-500 text-sm">
          <p>{t('auth.termsPrefix')}</p>
          <p>
            <a href="#" className="text-accent hover:underline">
              {t('auth.termsService')}
            </a>
            {' 和 '}
            <a href="#" className="text-accent hover:underline">
              {t('auth.termsPrivacy')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
